import React, { useEffect, useMemo } from 'react';
import { string } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { Typography } from '@ajaibid/components';
import { onDoRequestPriceOrderBookStock, onDoRequestPriceStock } from 'Redux/detailStock';

import { useStockBuySellContext } from '@screens/StockBuySell/StockBuySell/hooks';
import { useEventTracker as useBuySellTracker } from '@screens/StockBuySell/tracker';

import { OrderBookInfo, RunningOrderBook } from './OrderBook';
import { RunningTrade } from './RunningTrade';
import { styOrderBookInner } from '../style';
import IndicativeEquilibriumInfo from '../DetailedSection/IndicativeEquilibriumInfo';

const getOrderData = (stockOrderData, code, type, wantedLen) => {
  const orderData = stockOrderData?.[code]?.[type]?.items;
  if (!orderData || orderData.length === 0) return [];

  const orderItems = orderData.filter((item) => item.lot > 0).sort((a, b) => b.price - a.price);
  return orderItems.slice(0, wantedLen);
};

function OrderBookSection({ code }) {
  const tradingStatInfo = useSelector(
    (state) => state.detailStock.tradingStatDetailStockData?.[code]
  );
  const availableVolume = tradingStatInfo?.tradable_shares;
  const stockPriceData = useSelector((state) => state.detailStock?.priceStockData?.[code]);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(onDoRequestPriceOrderBookStock(code));
    dispatch(onDoRequestPriceStock(code));
  }, [code, availableVolume]);

  const stockOrderData = useSelector((state) => state.detailStock?.orderBookData);

  const bidOrderData = useMemo(
    () => getOrderData(stockOrderData, code, 'buy_side', 10),
    [stockOrderData, code]
  );
  const askOrderData = useMemo(
    () => getOrderData(stockOrderData, code, 'sell_side', 10),
    [stockOrderData, code]
  );

  const { formAction, dispatch: dispatchBuySellContext } = useStockBuySellContext();
  const { eventClickOrderBookPrice } = useBuySellTracker();

  const onClickRunningOrder = (order) => {
    eventClickOrderBookPrice();
    dispatchBuySellContext({
      type: formAction.CHANGE_PRICE,
      code,
      payload: order.price,
    });
  };

  return (
    <div className={styOrderBookInner}>
      <Typography bold type="body" size="lg" margin={0}>
        Order Book
      </Typography>
      <OrderBookInfo tradeData={tradingStatInfo} />
      <IndicativeEquilibriumInfo code={code} />
      <RunningOrderBook
        code={code}
        stockPriceData={stockPriceData}
        bidOrderData={bidOrderData}
        askOrderData={askOrderData}
        onClickRunningOrder={onClickRunningOrder}
      />
      <Typography type="body" size="lg" bold margin="24px 0 0">
        Running Trade {code}
      </Typography>
      <RunningTrade code={code} />
    </div>
  );
}

OrderBookSection.propTypes = {
  code: string,
};

OrderBookSection.defaultProps = {
  code: '',
};

export default OrderBookSection;
